import { Box, Button, IconButton, Toolbar } from "@mui/material";
import SearchInput from "../../tenant/search-input/search-input";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import styles from "./release-manage.module.scss";
import { useTranslation } from "react-i18next";
import ReleaseEditModal from "../release-edit-modal/release-edit-modal";
import DeleteReleaseModal from "../delete-release-modal/delete-release-modal";
import {
  createResource,
  updateResource,
  deleteResource,
  getResourceList,
} from "api/layer";
import {
  ReleaseNoteType,
  ReleaseDetail,
  ReleaseListItem,
  useFileDownload,
  getFileIcon,
  getFileExtension,
  getDisplayName,
} from "components/release";

// Map API response to ReleaseNoteType
const mapApiToReleaseNote = (item: any): ReleaseNoteType => {
  const attachments = item.attachments || [];
  const files = Array.isArray(attachments)
    ? attachments.map((attachment: string, index: number) => {
        const fileName = attachment.split("/").pop() || attachment;
        return {
          id: `f${index + 1}`,
          name: getDisplayName(attachment),
          size: "",
          type: getFileExtension(fileName),
          url: attachment,
        };
      })
    : undefined;

  return {
    id: String(item.resourceId),
    version: item.resourceVersion || "",
    title: item.resourceTitle || "",
    content: item.resourceContent || "",
    attachments: attachments,
    files,
    status: item.status || "draft",
  };
};

function ReleaseManage() {
  const [context, dispatch] = useContext(AppContext);
  const { t } = useTranslation();

  const [releaseNotes, setReleaseNotes] = useState<ReleaseNoteType[]>([]);
  const fetchReleaseList = async (): Promise<ReleaseNoteType[] | undefined> => {
    try {
      const res: any = await getResourceList({ keyword: searchKeyword });
      const details = res?.data?.releaseDetails || res?.releaseDetails || [];
      if (!Array.isArray(details)) return;
      // Filter only "Release Info" type resources
      const releaseInfoDetails = details.filter(
        (item: any) => item.resourceType === "Release Info"
      );
      const mapped = releaseInfoDetails.map(mapApiToReleaseNote);
      setReleaseNotes(mapped);
      return mapped;
    } catch (error) {
      console.error("Failed to fetch releases:", error);
      return undefined;
    }
  };

  const [selectedRelease, setSelectedRelease] =
    useState<ReleaseNoteType | null>(null);
  const [open, setOpen] = useState(false);
  const [currentRelease, setCurrentRelease] = useState<ReleaseNoteType | null>(
    null
  );
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  const { downloadingFile, handleDownloadFile } = useFileDownload({ dispatch });

  const filteredReleaseNotes = releaseNotes;

  useEffect(() => {
    fetchReleaseList();
  }, [searchKeyword]);

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
  };

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setShowFilters(false);
  };

  const handleReleaseClick = (release: ReleaseNoteType) => {
    setSelectedRelease(release);
  };

  const handleBackToList = () => {
    setSelectedRelease(null);
  };

  const handleSave = async (data: ReleaseNoteType) => {
    const payload: any = {
      resourceType: "Release Info",
      resourceVersion: data.version,
      resourceTitle: data.title,
      resourceContent: data.content,
      attachments: data.attachments || [],
      status: data.status || "draft",
    };

    const isEdit = !!currentRelease;
    try {
      let res: any;
      if (isEdit) {
        payload.resourceId = Number(data.id);
        res = await updateResource(payload);
      } else {
        res = await createResource(payload);
      }

      const code = res?.code;
      if (code === 200) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: isEdit ? "editRelease" : "addRelease",
            message: isEdit ? "editReleaseSuccess" : "addReleaseSuccess",
          },
        });
      } else if (code === 400) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: isEdit ? "editRelease" : "addRelease",
            message: isEdit ? "editReleaseWarning" : "addReleaseWarning",
            titleIconColor: "yellow",
            titleIcon: "warning",
          },
        });
      } else {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: isEdit ? "editRelease" : "addRelease",
            message: isEdit ? "editReleaseError" : "addReleaseError",
            titleIconColor: "red",
            titleIcon: "error",
          },
        });
      }

      const updated = await fetchReleaseList();
      if (selectedRelease && selectedRelease.id === data.id && updated) {
        const refreshed = updated.find((r) => r.id === data.id);
        if (refreshed) setSelectedRelease(refreshed);
      }
    } catch (error) {
      console.error("Failed to save release:", error);
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: isEdit ? "editRelease" : "addRelease",
          message: isEdit ? "editReleaseError" : "addReleaseError",
          titleIconColor: "red",
          titleIcon: "error",
        },
      });
    } finally {
      setOpen(false);
      setCurrentRelease(null);
    }
  };

  const handleDelete = (release: ReleaseNoteType) => {
    setCurrentRelease(release);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!currentRelease) return;
    try {
      const res: any = await deleteResource(Number(currentRelease.id));

      const code = res?.code;
      if (code === 200) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "deleteRelease",
            message: "deleteReleaseSuccess",
          },
        });
      } else if (code === 400) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "deleteRelease",
            message: "deleteReleaseWarning",
            titleIconColor: "yellow",
            titleIcon: "warning",
          },
        });
      } else {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "deleteRelease",
            message: "deleteReleaseError",
            titleIconColor: "red",
            titleIcon: "error",
          },
        });
      }

      await fetchReleaseList();
      if (selectedRelease && selectedRelease.id === currentRelease.id) {
        setSelectedRelease(null);
      }
    } catch (error) {
      console.error("Failed to delete release:", error);
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "deleteRelease",
          message: "deleteReleaseError",
          titleIconColor: "red",
          titleIcon: "error",
        },
      });
    } finally {
      setCurrentRelease(null);
      setOpenDeleteModal(false);
    }
  };

  if (selectedRelease) {
    const adminActions = context.menu?.find((item) => item.id === "100002") ? (
      <div className="flex gap-2 items-center">
        <IconButton
          onClick={() => {
            setCurrentRelease(selectedRelease);
            setOpen(true);
          }}
          className="!text-[#00463B]"
          aria-label="edit"
        >
          <span className="material-icons">edit</span>
        </IconButton>
        <IconButton
          onClick={() => handleDelete(selectedRelease)}
          className="!text-[#d32f2f]"
          aria-label="delete"
        >
          <span className="material-icons">delete</span>
        </IconButton>
      </div>
    ) : null;

    return (
      <>
        <ReleaseDetail
          release={selectedRelease}
          downloadingFile={downloadingFile}
          onDownloadFile={handleDownloadFile}
          attachmentsLabel={t("releases.attachments")}
          containerClassName={styles.container}
          actionsContent={
            <div className="mb-6 flex justify-between items-center w-full">
              <IconButton
                onClick={handleBackToList}
                className="!text-[#00463B]"
                aria-label="back"
              >
                <span className="material-icons text-[24px]">arrow_back</span>
                <span className="ml-2 text-base">{t("buttons.back")}</span>
              </IconButton>
              {adminActions}
            </div>
          }
        />

        {open && (
          <ReleaseEditModal
            open={open}
            onClose={() => {
              setOpen(false);
              setCurrentRelease(null);
            }}
            onSave={handleSave}
            data={currentRelease || undefined}
            fileIconGetter={getFileIcon}
          />
        )}

        {openDeleteModal && currentRelease && (
          <DeleteReleaseModal
            data={currentRelease}
            onConfirm={confirmDelete}
            onClose={() => {
              setOpenDeleteModal(false);
              setCurrentRelease(null);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className={styles.container}>
      <Toolbar
        style={{
          justifyContent: "space-between",
          borderBottom: "1px solid #ccc",
          marginBottom: "5px",
          padding: "0",
        }}
      >
        {context.menu && context.menu.find((item) => item.id === "100002") && (
          <Button
            variant="contained"
            color="success"
            size="small"
            endIcon={<span className="material-icons">add</span>}
            onClick={() => {
              setOpen(true);
              setCurrentRelease(null);
            }}
          >
            {t("userSystem.addReleaseNote")}
          </Button>
        )}
        <IconButton
          color="success"
          onClick={toggleFilters}
          style={{
            color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
            background: showFilters ? "#c1e5ca" : "",
          }}
        >
          <span className="material-icons">filter_list</span>
        </IconButton>
      </Toolbar>

      {showFilters && (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#f5f5f5",
            justifyContent: "space-between",
          }}
        >
          <Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
            <SearchInput onSearch={handleSearch} />
          </Box>
          <IconButton onClick={clearFilters} aria-label="clear" color="default">
            <span className="material-icons">clear</span>
          </IconButton>
        </Box>
      )}

      <div style={{ flex: 0.95, overflow: "auto" }}>
        <div className="w-full h-auto py-4 flex flex-col gap-6">
          {filteredReleaseNotes.map((release) => (
            <ReleaseListItem
              key={release.id}
              release={release}
              onClick={handleReleaseClick}
              className="w-full p-4 border-y border-[#bbb] relative cursor-pointer hover:bg-gray-50 transition-colors"
            />
          ))}
        </div>
      </div>

      {open && (
        <ReleaseEditModal
          open={open}
          onClose={() => {
            setOpen(false);
            setCurrentRelease(null);
          }}
          onSave={handleSave}
          data={currentRelease || undefined}
          fileIconGetter={getFileIcon}
        />
      )}

      {openDeleteModal && currentRelease && (
        <DeleteReleaseModal
          data={currentRelease}
          onConfirm={confirmDelete}
          onClose={() => {
            setOpenDeleteModal(false);
            setCurrentRelease(null);
          }}
        />
      )}
    </div>
  );
}

export default ReleaseManage;
